<template>
  <v-container id="usersList">
    <v-overlay class="waitoverlay" :value="busy">
      <v-progress-circular
        indeterminate
        size="64"
        class="mr-4"
      /> {{ waitMessage }}
    </v-overlay>
    <v-card>
      <v-card-title>
        <v-row>
          <v-col cols="12" sm="8" md="6">
            {{ title }}  <span v-if="isSeatLimit && !loading" class="sm">{{ $t('users.list.seats-remain', [seatsRemaining]) }}</span>
          </v-col>
          <v-col cols="12" sm="4" md="6" class="text-sm-right">
            <v-btn color="primary" :disabled="quotaFull" @click="createItemDialog">
              {{ $t('users.add-user') }}
            </v-btn>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="secondary"
                  :aria-label="$t('users.menu')"
                  v-bind="attrs"
                  v-on="on"
                  @click="focusFirstItem"
                >
                  <v-icon class="">
                    mdi-menu
                  </v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item ref="firstItem" tabindex="0">
                  <v-btn color="secondary" :text="true" block @click="confirmInviteAll">
                    {{ $t('users.send-invitations') }}
                  </v-btn>
                </v-list-item>
                <v-list-item tabindex="0">
                  <v-btn :block="true" color="secondary" :text="true" @click="exportData">
                    {{ $t('export-users') }}
                  </v-btn>
                </v-list-item>
                <v-list-item tabindex="0">
                  <v-btn v-if="csvimport" :block="true" :text="true" :disabled="quotaFull" color="secondary" @click="$router.push('/admin/users/import')">
  {{ $t('users.import-users') }}
</v-btn>
                </v-list-item>
                <v-list-item tabindex="0">
                  <v-btn :block="true" :text="true" :disabled="selected.length === 0" color="secondary" @click="validateDeleteSelection">
                    {{ $t('delete-selected-users', [selected.length>0? `(${selected.length})` : '']) }}
                  </v-btn>
                </v-list-item>
                <v-list-item v-if="userHasResetLinkPermission" tabindex="0">
                  <v-btn :block="true" :text="true" color="secondary" @click="generateResetPasswordLink">
                    Generate reset link
                  </v-btn>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text width="100%">
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('global.search')"
              :aria-label="$t('global.text-field')+' '+$t('global.search') +' '+ $t('users.users')"
              single-line
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6" md="8" class="d-flex align-center">
            <v-chip
              v-if="dateFilterActive"
              close
              color="primary"
              outlined
              class="mr-2 datepicker"
              @click:close="clearDateFilter"
            >
              <v-icon left small>
                mdi-calendar-range
              </v-icon>
              {{ formatDateRange(startDate, endDate) }}
            </v-chip>
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  color="secondary"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left small>
                    mdi-calendar-filter
                  </v-icon>
                  {{ $t('users.header.filter-by-date') }}
                </v-btn>
              </template>
              <v-date-picker
                v-model="tempDates"
                range
                no-title
                scrollable
                @click:date="onDateClick"
              >
                <v-spacer />
                <v-btn
                  text
                  @click="cancelDateFilter"
                >
                  {{ $t('global.cancel') }}
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="applyDateFilter"
                >
                  {{ $t('global.confirm') }}
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-data-table
          v-model="selected"
          filterable
          :headers="computedHeaders.map((x) => { return { ...x, text: $t(x.text) } })"
          :items="filteredItems"
          item-key="id"
          :loading="loading"
          :search="search"
          show-select
          :items-per-page="30"
          :footer-props="{'items-per-page-options':[15, 30, 50, 100, -1]}"
        >
          <template v-slot:item.displayName="{ item }">
            <a v-if="userlinks" :href="createURL(item)">
              {{ item.displayName }}
            </a>
            <span v-if="!userlinks">{{ item.displayName }}</span>
          </template>
          <template v-slot:item.roles="{ item }">
            <v-chip :class="getHighestRoleOf(item)">
              {{ $t('role.'+getHighestRoleOf(item)) }}
            </v-chip>
          </template>
          <template v-slot:item.status="{ item }">
            {{ statusText(item.status) }}
          </template>
          <template v-slot:item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-icon v-if="hasEditPermissions(item)"
                  :aria-label="$t('global.edit') + ' ' + $t('users.userParam', [item.displayName])"
                  class="mr-2"
                  v-on="on"
                  @click="editItem(item)"
                >
                  mdi-pencil-outline
                </v-icon>
              </template>
              <span>{{ $t('edit') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-icon v-if="item.id !== $store.getters.currentUserProfile.user.id && hasEditPermissions(item) && hasDeletePermissions(item)"
                  class="mr-2"
                  :aria-label="$t('global.delete') + ' ' + $t('users.userParam', [item.displayName])"
                  v-on="on"
                  @click="deleteItem(item)"
                >
                  mdi-delete-outline
                </v-icon>
              </template>
              <span>{{ $t('delete') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-icon v-if="item.status!=='registered' && item.status!=='invited' && item.email"
                  :aria-label="$t('users.invite', [item.displayName])"
                  v-on="on"
                  @click="inviteUser(item)"
                >
                  mdi-email-outline
                </v-icon>
              </template>
              <span>{{ $t('invite-unregistered') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-icon v-if="item.status=='invited'"
                  :aria-label="$t('users.inviteAgain', [item.displayName])"
                  v-on="on"
                  @click="inviteUser(item)"
                >
                  mdi-email-multiple-outline
                </v-icon>
              </template>
              <span>{{ $t('invite') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-row justify="center">
      <v-dialog v-model="showEditDialog" max-width="700px">
        <user-edit
          :model="editedItem"
          :roles="roles"
          :content-selection="content"
          @cancel="closeEditDialog"
          @save="saveEditDialog"
        />
      </v-dialog>
      <v-dialog v-model="showDeleteDenied" max-width="500px">
        <v-card>
          <v-card-title>{{ $t('users.cant-delete-user') }}<br></v-card-title>
          <v-card-text>
            {{ $t('users.cant-delete-user-content', [editedItem.displayName]) }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="blue darken-1" @click="showDeleteDenied = false">
             OK
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showDeleteDialog" max-width="500px">
        <v-card>
          <v-card-title>{{ $t('users.confirm-delete', [editedItem.displayName]) }}<br></v-card-title>
          <v-card-text v-html="$t('users.list.delete-warning')" />
          <v-card-actions>
            <v-spacer />
            <v-btn @click="closeDeleteDialog">
              {{ $t('global.cancel') }}
            </v-btn>
            <v-btn color="red" @click="confirmDeleteItem">
              {{ $t('global.confirm') }}
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showErrorDialog" max-width="500px">
        <v-card>
          <v-card-title>{{ $t('global.error') }}</v-card-title>
          <v-card-text>{{ $t('users.error-message') }}<br>"{{ error }}"</v-card-text>
          <v-card-actions>
            <v-btn color="blue darken-1" @click="closeErrorDialog">
              OK
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="inviteAllDialogVisible" max-width="700px">
        <v-card>
          <v-card-title>{{ $t('users.confirm-delete', [editedItem.displayName]) }}<br></v-card-title>
          <v-card-text v-html="$t('users.list.delete-warning')" />
          <v-card-actions>
            <v-spacer />
            <v-btn @click="closeDeleteDialog">
              {{ $t('global.cancel') }}
            </v-btn>
            <v-btn color="red" @click="confirmDeleteItem">
              {{ $t('global.confirm') }}
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showErrorDialog" max-width="500px">
        <v-card>
          <v-card-title>{{ $t('global.error') }}</v-card-title>
          <v-card-text>{{ $t('users.error-message') }}<br>"{{ error }}"</v-card-text>
          <v-card-actions>
            <v-btn color="blue darken-1" @click="closeErrorDialog">
              OK
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="inviteAllDialogVisible" max-width="700px">
        <v-card>
          <v-card-title>{{ $t('users.bulk-user-invitations') }}</v-card-title>
          <v-card-text>
            <p v-html="$t('users.confirm-invite')" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="secondary" @click="cancelInviteAll">
              {{ $t('global.cancel') }}
            </v-btn>
            <v-btn color="primary" @click="inviteAll(true)">
              {{ $t('users.uninvited') }}
            </v-btn>
            <v-btn color="error" @click="inviteAll(false)">
              {{ $t('users.all') }}
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="resetPasswordLinkDialogVisible" max-width="700px">
        <v-card>
          <v-card-title>Generate rest password link</v-card-title>
          <v-form ref="form" v-model="valid">
            <v-card-text>
              <v-text-field
                v-model="email"
                :label="$t('user-email')"
                :rules="[rules.emailRules]"
                required
              />
              <a :href="link" :style="styleForTag">
                {{ link }}
              </a>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn color="secondary" @click="cancelGenerationResetPassword">
                {{ $t('global.cancel') }}
              </v-btn>
              <v-btn color="primary" :disabled="!valid" @click="generationResetPasswordLink">
                Generate
              </v-btn>
              <v-spacer />
            </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showDeleteSelectedConfirm" max-width="700px">
        <v-card>
          <v-card-title>{{ $t('deleted-selected-users-title') }}</v-card-title>
          <v-card-text>
            <p v-if="selected.length===0" class="text-center" v-html="$t('users.confirm-selected-users-if-no-user-selected')" />
            <p v-if="selected.length>0" class="text-center" v-html="$t('users.confirm-selected-users', [selected.length])" />
          </v-card-text>
          <v-card-actions v-if="selected.length>0">
            <v-spacer />
            <v-btn color="secondary" @click="showDeleteSelectedConfirm = false">
              {{ $t('global.cancel') }}
            </v-btn>
            <v-btn color="error" @click="deleteUserSelection">
              {{ $t('global.confirm') }}
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
  </v-container>
</template>

<script>

  import api from '@/helpers/api.js'
  import userEdit from './UserEdit.vue'
  import { Users, Roles } from '@/helpers/users.js'
  import { ContentLibrary } from '@/helpers/contentLibrary.js'
  import moment from 'moment'

  export default {
    name: 'UserList',

    components: {
      'user-edit': userEdit,
    },

    props: {
      title: { type: String, required: false, default: 'Users' },
      roles: { type: Array, required: false, default: undefined },
      csvimport: { type: Boolean, required: false, default: false },
      userlinks: { type: Boolean, required: false, default: false },
      account: { type: String, required: false, default: undefined },
    },

    data () {
      return {
        loading: false,
        showEditDialog: false,
        showDeleteDialog: false,
        error: undefined,
        busy: false,
        editedIndex: -1,
        editedItem: {},
        valid: false,
        email: '',
        link: '',
        styleForTag: 'visibility: hidden;',
        defaultItem: {
          displayName: '',
          roles: undefined,
          email: '',
          id: undefined,
          content: [],
        },
        rules: {
          emailRules: value => {
            const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            if (!value) {
              return true
            }
            return pattern.test(value) || this.$t('forgotPassword.invalid-e-mail')
          },
        },
        rowsPerPage: 15,
        search: '',
        selected: [],
        items: [],
        headers: [
          { text: 'users.header.name', sortable: true, value: 'displayName' },
          { text: 'users.header.email', sortable: true, value: 'email' },
          {
            text: 'users.header.role',
            value: 'roles',
            sortable: true,
            sort: (a, b) => {
              return Roles.HighestOf(a).localeCompare(Roles.HighestOf(b))
            },
          },
          { text: 'users.header.registration-status', value: 'status' },
          { text: 'users.header.tag', value: 'tag' },
          { text: 'users.header.created', value: 'createdAt', sortable: true },
          { text: 'users.header.actions', value: 'actions', sortable: false },

        ],
        waitMessage: this.$t('users.header.please-wait'),
        inviteAllDialogVisible: false,
        showDeleteSelectedConfirm: false,
        showDeleteDenied: false,
        resetPasswordLinkDialogVisible: false,
        totalUsers: 0,
        startDate: null,
        endDate: null,
        dateMenu: false,
        tempDates: [],
      }
    },

    computed: {
      seatsRemaining () {
        if (this.$store.getters.currentUserProfile.account.maxUsers === 0) return 'unlimited'
        return this.$store.getters.currentUserProfile.account.maxUsers - this.totalUsers
      },
      userHasResetLinkPermission () {
        return this.$store.getters.currentUserProfile.user.roles.includes(Roles.SYSADMIN)
      },
      showErrorDialog () {
        return this.error !== undefined
      },
      content () {
        return this.$store.state.contentLibrary ? this.$store.state.contentLibrary.filter((x) => x.purchased) : []
      },
      computedHeaders () {
        return this.headers.filter(x => { return this.role === undefined || x.value !== 'role' })
      },
      quotaFull () {
        const maxUsers = this.$store.getters.currentUserProfile.account.maxUsers
        return (this.items.length >= maxUsers) && maxUsers !== 0
      },
      isSeatLimit () {
        return this.$store.getters.currentUserProfile.account.maxUsers > 0
      },
      dateFilterActive () {
        return this.startDate !== null && this.endDate !== null
      },
      filteredItems () {
        if (!this.dateFilterActive) {
          return this.items
        }
        return this.items.filter(item => {
          if (!item.createdAt) return false
          const createdDate = moment(item.createdAt)
          return createdDate.isSameOrAfter(moment(this.startDate), 'day') &&
            createdDate.isSameOrBefore(moment(this.endDate), 'day')
        })
      },
    },

    watch: {
      dateMenu (newVal) {
        if (newVal) {
          // When opening the menu
          if (this.dateFilterActive) {
            // Populate tempDates with current filter when editing
            this.tempDates = [
              moment(this.startDate).format('YYYY-MM-DD'),
              moment(this.endDate).format('YYYY-MM-DD'),
            ]
          } else {
            // Clear tempDates when opening for a new filter
            this.tempDates = []
          }
        }
      },
    },

    async mounted () {
      try {
        // Ensure ContentLibrary is loaded first to prevent race conditions
        await this.refreshItems()
      } catch (error) {
        console.error('Error loading UsersList component:', error)
      }
    },

    methods: {
      focusFirstItem () {
        this.$nextTick(() => {
          this.$refs.firstItem.$el.focus()
        })
      },
      async exportData () {
        // Call api get users stats
        const userProgress = await api.getUsersStats(this.$store.getters.currentUserProfile.user.account)
        // console.dir(userProgress)

        // remove any entries where the key is not a user id
        for (const key of Object.keys(userProgress)) {
          if (key === '') {
            delete userProgress[key]
          }
        }

        // userProgress is a map, each key is a user id, each value is a map of modules to completion bool
        // e.g. { "user1": { "module1": true, "module2": false }, "user2": { "module1": true, "module2": true } }

        // first get all unique modules
        const modules = new Set()
        for (const user of Object.values(userProgress)) {
          for (const module of Object.keys(user)) {
            modules.add(module)
          }
        }

        // next create a table of user progress, with user as each row, and a column per module
        const table = []

        // get the content library if not already loaded
        // then convert the modules to their display names
        if (!this.$store.state.contentLibrary) {
          const contents = await api.getContents()
          this.$store.commit('SET_CONTENT_LIBRARY', contents)
        }
        // get the localised name of each module
        const moduleNames = []
        modules.forEach((module) => {
          try {
            moduleNames.push(ContentLibrary.NameOf(this.$store, this.$i18n.locale, module) ?? module)
          } catch (error) {
            console.warn('Error translating module name:', module, error)
            moduleNames.push(module)
          }
        })
        // console.dir(moduleNames)

        table.push(['User', 'Email', 'Role', 'Registration', 'Tag'].concat(moduleNames))
        for (const [userId, user] of Object.entries(userProgress)) {
          // convert user id to user name
          const userData = this.items.find(x => x.id === userId)
          if (!userData) {
            continue
          }
          const row = [userData.displayName ?? '', userData.email ?? '', this.getRole(userData), this.statusText(userData.status), userData.tag ?? '']
          for (const module of modules) {
            row.push(user[module] ? 'Complete' : user[module] === undefined ? '' : 'Started')
          }
          table.push(row)
        }

        // console.dir(table)

        // convert array (table) into csv
        const csv = table.map(row => row.map(x => `"${x}"`).join(',')).join('\n')

        const anchor = document.createElement('a')
        anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
        anchor.target = '_blank'
        anchor.download = 'Users Export.csv'
        anchor.click()
      },
      getRole (row) {
        return row.roles.join(';')
      },
      validateDeleteSelection () {
        this.showDeleteSelectedConfirm = true
      },
      async deleteUserSelection () {
        this.showDeleteSelectedConfirm = false
        this.busy = true
        await api.deleteUser(this.selected.map(x => x.id))
        this.totalUsers -= this.selected.length
        this.items = this.items.filter(item => !this.selected.includes(item))
        this.selected = [] // clear list
        this.busy = false
      },
      hasEditPermissions (item) {
        const yourMaxRole = Roles.All.reduce((prev, current) => (this.$store.getters.currentUserProfile.user.roles.includes(current) ? current : prev))
        const theirMaxRole = Roles.All.reduce((prev, current) => item.roles.includes(current) ? current : prev)
        const yourMaxRoleIndex = Roles.All.indexOf(yourMaxRole)
        const theirMaxRoleIndex = Roles.All.indexOf(theirMaxRole)
        return yourMaxRoleIndex >= theirMaxRoleIndex
        // filter off ones above
      },
      hasDeletePermissions (item) {
        return this.hasEditPermissions(item) // this.$store.getters.currentUserProfile.user.roles.includes(Roles.ADMIN)
      },
      statusText (status) {
        if (status === 'unregistered') return this.$t('users.status.unregistered')
        if (status === 'invited') return this.$t('users.status.invite-sent')
        if (status === 'registered') return this.$t('users.status.registered')
        if (status === 'active') return this.$t('users.status.active')
        return status.toUpperCase()
      },
      formatDate (date) {
        if (!date) return ''
        const validDate = new Date(date)
        if (isNaN(validDate.getTime())) return ''
        return this.$i18n.d(validDate, 'short')
      },
      closeErrorDialog () {
        this.error = undefined
      },
      async createItemDialog () {
        this.editedIndex = -1
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedItem.roles = this.roles ? ['learner'].concat(this.roles) : ['learner']
        this.showEditDialog = true
      },
      async refreshItems () {
        this.loading = true
        if (this.account !== this.$store.getters.currentUserProfile.user.account) {
          this.items = await api.getUsers(this.account, this.role)
          // console.log(this.items)
        } else {
          Users.SetDirty()
          this.items = await Users.Get(this.roles)
        }
        // console.log(JSON.stringify(this.items))
        // var users = await api.getUsers(this.account, this.role)
        if (!this.items) {
          this.items = []
        }
        this.loading = false
        this.totalUsers = this.$store.getters.currentUserProfile.account.totalUsers
      },
      createURL (item) {
        return '#/admin/users/' + item.id
      },
      async editItem (item) {
        this.busy = true
        this.editedIndex = this.items.indexOf(item)
        this.editedItem = await api.getUser(item.id)
        this.editedItem.status = item.status
        if (!this.editedItem.content) {
          this.editedItem.content = []
        }
        this.busy = false
        this.showEditDialog = true
      },
      closeEditDialog () {
        this.showEditDialog = false
      },
      async saveEditDialog () {
        this.showEditDialog = false
        delete this.editedItem.status
        try {
          if (this.editedIndex === -1) {
            if (this.account) this.editedItem.account = this.account
            this.busy = true
            const record = await api.createUser(this.editedItem)
            this.totalUsers += 1
            Users.SetDirty()
            this.busy = false
            this.editedItem.id = record.id
            this.items.push({ id: record.id, displayName: record.displayName, email: record.email, roles: record.roles, status: record.status, content: record.content })
          }
          else
          {
            this.busy = true
            await api.updateUser(this.editedItem.id, this.editedItem)
            Users.SetDirty()
            Object.assign(this.items[this.editedIndex], this.editedItem)
            this.busy = false
          }
        } catch (ex) {
          this.error = ex.response.data // ex.response.data
          console.error('request failed with: ' + ex)
          this.busy = false
        }
      },
      async confirmDeleteItem () {
        this.closeDeleteDialog()
        if (this.items[this.editedIndex].status !== 'active') {
          this.totalUsers -= 1
        }
        this.items.splice(this.editedIndex, 1)
        this.busy = true
        const userId = []
        userId.push(this.editedItem.id)
        await api.deleteUser(userId)
        Users.SetDirty()
        this.busy = false
      },
      async inviteUser (item) {
        this.busy = true
        try {
          const returnURL = process.env.VUE_APP_URL + '/register'
          await api.inviteUser(item.id, returnURL)
        } catch (ex) {
          this.error = ex.response.data
          console.error('request failed with ' + ex)
        }
        this.busy = false
        await this.refreshItems()
      },
      deleteItem (item) {
        this.editedItem = item
        this.editedIndex = this.items.indexOf(item)
        this.showDeleteDialog = true
      },
      closeDeleteDialog () {
        this.showDeleteDialog = false
        this.$nextTick(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
        })
      },
      getHighestRoleOf (item) {
        return Roles.HighestOf(item.roles)
      },

      generateResetPasswordLink () {
        this.styleForTag = 'visibility: hidden;'
        this.email = ''
        this.link = ''
        this.resetPasswordLinkDialogVisible = true
      },
      async generationResetPasswordLink () {
        if (this.email.length > 0) {
          this.busy = true
          this.link = 'Loading....'
          this.valid = false
          const response = await api.generationResetPasswordLink(this.email)
          this.link = response.link
          this.styleForTag = 'visibility: visible;'
          this.valid = true
          this.busy = false
        }
      },
      cancelGenerationResetPassword () {
        this.resetPasswordLinkDialogVisible = false
      },
      cancelDeleteSelection () {
        this.deleteSelectedVisible = false
      },
      confirmInviteAll () {
        this.inviteAllDialogVisible = true
      },
      cancelInviteAll () {
        this.inviteAllDialogVisible = false
      },
      async inviteAll (excludeInvited) {
        this.inviteAllDialogVisible = false
        this.busy = true
        const returnURL = process.env.VUE_APP_URL + '/register'
        await api.usersInviteAll(excludeInvited, returnURL)
        this.busy = false
        await this.refreshItems()
      },
      onDateClick () {
        // Prevent the menu from closing when selecting dates
        // The menu will only close when user clicks Cancel or Confirm
      },
      applyDateFilter () {
        if (this.tempDates && this.tempDates.length === 2) {
          // Sort dates to ensure start is before end
          const sortedDates = [...this.tempDates].sort()
          this.startDate = moment(sortedDates[0]).toDate()
          this.endDate = moment(sortedDates[1]).toDate()
        }
        this.dateMenu = false
        // Don't clear tempDates here - the watcher will handle it on next open
      },
      cancelDateFilter () {
        this.dateMenu = false
        // Don't clear tempDates here - the watcher will handle it on next open
      },
      clearDateFilter () {
        this.startDate = null
        this.endDate = null
        // tempDates will be cleared by watcher when menu opens next time
      },
      formatDateRange (start, end) {
        if (!start || !end) return ''
        return `${moment(start).format('DD/MM/YYYY')} - ${moment(end).format('DD/MM/YYYY')}`
      },
    },
  }
</script>


<style scoped>
.super { background-color: #ff0000 !important; color: black; }
.developer { background-color: #272727 !important; color: white; }
.coach { background-color: #3dbfb7 !important; color: black; }
.admin { background-color: #ffa40c !important; color: black; }
.sysadmin { background-color: #5b9256 !important; color: black; }
.checkbox {margin-bottom: -2px !important; height: 40px!important}


.sm {
  font-size: 0.7em;
}
</style>
